import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cabApi } from '../../services/api'
import toast from 'react-hot-toast'
import { Barcode, PackageCheck, XCircle, Boxes, RefreshCcw, Printer, Download } from 'lucide-react'

type Operazione = 'RIPARATO_CON_CAB' | 'RIPARATO_SENZA_CAB' | 'NON_RIPARABILE' | 'PALLET' | 'ANTICIPO_RIPARAZIONE'

const OPERAZIONI: { key: Operazione; label: string; icon: React.ElementType }[] = [
  { key: 'RIPARATO_CON_CAB', label: 'Riparato · con CAB', icon: Barcode },
  { key: 'RIPARATO_SENZA_CAB', label: 'Riparato · senza CAB', icon: PackageCheck },
  { key: 'NON_RIPARABILE', label: 'Non riparabile', icon: XCircle },
  { key: 'PALLET', label: 'Pallet / imballo', icon: Boxes },
  { key: 'ANTICIPO_RIPARAZIONE', label: 'Anticipo riparazione', icon: RefreshCcw },
]

const oggiMMAA = () => {
  const d = new Date()
  return `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getFullYear() % 100).padStart(2, '0')}`
}

export default function CabNuovaEtichetta() {
  const qc = useQueryClient()
  const [operazione, setOperazione] = useState<Operazione>('RIPARATO_CON_CAB')
  const [form, setForm] = useState<Record<string, string>>({ mmaa: oggiMMAA(), stato: 'N', quantita: '', sotto_caso_anticipo: 'B' })
  const [avvisiConfermati, setAvvisiConfermati] = useState<Set<string>>(new Set())
  const [copie, setCopie] = useState(1)
  const [stampanteId, setStampanteId] = useState<number | ''>('')
  const [inviata, setInviata] = useState<{ stampante: string } | null>(null)
  const [ultimaEtichettaId, setUltimaEtichettaId] = useState<string | null>(null)

  const f = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => { setAvvisiConfermati(new Set()); setInviata(null); setUltimaEtichettaId(null) }, [operazione, form.cab_originale, form.nmu, form.quantita, form.progressivo])

  const { data: stampanti } = useQuery({ queryKey: ['cab-stampanti'], queryFn: () => cabApi.stampanti.list().then(r => r.data) })

  const payloadBase = () => {
    const p: Record<string, unknown> = { operazione, ...form }
    if (form.quantita) p.quantita = parseInt(form.quantita, 10)
    if (form.progressivo_manuale === 'true') p.progressivo_manuale = true
    if (avvisiConfermati.size) p.conferma_duplicato = true
    return p
  }

  const { data: anteprima, isFetching: caricandoAnteprima } = useQuery({
    queryKey: ['cab-anteprima', operazione, form, Array.from(avvisiConfermati)],
    queryFn: () => cabApi.anteprima(payloadBase()).then(r => r.data),
    enabled: true,
  })

  const creaMut = useMutation({
    mutationFn: () => cabApi.crea({
      ...payloadBase(),
      avvisi_confermati: Array.from(avvisiConfermati),
      stampa: !!stampanteId,
      stampante_id: stampanteId || undefined,
      copie,
    }).then(r => r.data),
    onSuccess: (data) => {
      toast.success('Etichetta registrata')
      qc.invalidateQueries({ queryKey: ['cab-etichette'] })
      if (stampanteId) setInviata({ stampante: 'inviata alla stampante' })
      setUltimaEtichettaId(data?.id ?? null)
      setForm(p => ({ ...p, cab_originale: '', nmu: operazione === 'PALLET' ? p.nmu : '', progressivo: '' }))
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail
      if (detail?.avvisi) {
        toast.error('Confermare gli avvisi prima di procedere')
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore nella creazione')
      }
    },
  })

  const modelloTrattenutoMut = useMutation({
    mutationFn: () => cabApi.modelloTrattenuto(ultimaEtichettaId as string),
    onSuccess: (res) => {
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url; a.download = 'modello_materiale_trattenuto.pdf'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Errore nella generazione del modello PDF'),
  })

  const avvisi: string[] = anteprima?.avvisi ?? []
  const errori: string[] = anteprima?.errori ?? []
  const tuttiConfermati = avvisi.every(a => avvisiConfermati.has(a))
  const puoProcedere = errori.length === 0 && (anteprima?.genera_etichetta ?? true) !== false ? true : operazione === 'NON_RIPARABILE'
  const disabilitaGenera = errori.length > 0 || creaMut.isPending || (operazione !== 'NON_RIPARABILE' && !anteprima?.codice)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Nuova Etichetta CAB</h1>
          <p className="text-sm text-slate-500">Compila la maschera guidata per generare l'etichetta a codice a barre.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {OPERAZIONI.map(op => (
            <button key={op.key} onClick={() => { setOperazione(op.key); setForm({ mmaa: oggiMMAA(), stato: 'N' }) }}
              className={`card p-3 flex flex-col items-center gap-1.5 text-xs font-medium transition-colors ${operazione === op.key ? 'ring-2' : ''}`}
              style={{ borderColor: operazione === op.key ? 'var(--mac-blue)' : undefined, color: operazione === op.key ? 'var(--mac-blue)' : '#475569' }}>
              <op.icon size={18} />
              {op.label}
            </button>
          ))}
        </div>

        <div className="card p-4 space-y-3">
          {operazione === 'RIPARATO_CON_CAB' && (
            <>
              <div>
                <label className="label">Scansiona CAB originale</label>
                <input autoFocus className="input font-mono" value={form.cab_originale ?? ''} onChange={e => f('cab_originale', e.target.value.toUpperCase())} placeholder="%17NL651497N110110029E" />
              </div>
              <div>
                <label className="label">Data riparazione (MMAA)</label>
                <input className="input font-mono w-32" value={form.mmaa ?? ''} onChange={e => f('mmaa', e.target.value)} maxLength={4} />
              </div>
            </>
          )}

          {operazione === 'RIPARATO_SENZA_CAB' && (
            <>
              <div><label className="label">NMU</label><input className="input font-mono" value={form.nmu ?? ''} onChange={e => f('nmu', e.target.value)} maxLength={6} /></div>
              <div><label className="label">Data riparazione (MMAA)</label><input className="input font-mono w-32" value={form.mmaa ?? ''} onChange={e => f('mmaa', e.target.value)} maxLength={4} /></div>
              <p className="text-xs text-slate-400">Verrà generato: stato R, stabilimento 0, seriale 00000.</p>
            </>
          )}

          {operazione === 'NON_RIPARABILE' && (
            <>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                NON applicare etichette: restituire al CDR senza modificare il CAB.
              </div>
              <div><label className="label">CAB originale (facoltativo)</label><input className="input font-mono" value={form.cab_originale ?? ''} onChange={e => f('cab_originale', e.target.value.toUpperCase())} /></div>
              <div><label className="label">Motivo *</label><textarea className="input" rows={2} value={form.motivo ?? ''} onChange={e => f('motivo', e.target.value)} /></div>
            </>
          )}

          {operazione === 'PALLET' && (
            <>
              <div><label className="label">NMU</label><input className="input font-mono" value={form.nmu ?? ''} onChange={e => f('nmu', e.target.value)} maxLength={6} /></div>
              <div>
                <label className="label">Stato</label>
                <select className="select w-32" value={form.stato ?? 'R'} onChange={e => f('stato', e.target.value)}>
                  {['N', 'R', 'A', 'B', 'C', 'D'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="label">Quantità pezzi</label><input className="input w-32" type="number" min={1} value={form.quantita ?? ''} onChange={e => f('quantita', e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.progressivo_manuale === 'true'} onChange={e => f('progressivo_manuale', String(e.target.checked))} />
                Progressivo manuale
              </label>
              {form.progressivo_manuale === 'true' && (
                <input className="input w-32 font-mono" value={form.progressivo ?? ''} onChange={e => f('progressivo', e.target.value.toUpperCase())} maxLength={5} placeholder="00001" />
              )}
            </>
          )}

          {operazione === 'ANTICIPO_RIPARAZIONE' && (
            <>
              <div className="flex gap-2">
                <button className={`btn-secondary text-xs ${form.sotto_caso_anticipo === 'A' ? 'ring-2' : ''}`} onClick={() => f('sotto_caso_anticipo', 'A')}>(a) con CAB</button>
                <button className={`btn-secondary text-xs ${form.sotto_caso_anticipo === 'B' ? 'ring-2' : ''}`} onClick={() => f('sotto_caso_anticipo', 'B')}>(b) senza CAB</button>
              </div>
              {form.sotto_caso_anticipo === 'A' ? (
                <div><label className="label">CAB del materiale di proprietà</label><input className="input font-mono" value={form.cab_originale ?? ''} onChange={e => f('cab_originale', e.target.value.toUpperCase())} /></div>
              ) : (
                <>
                  <div><label className="label">NMU</label><input className="input font-mono" value={form.nmu ?? ''} onChange={e => f('nmu', e.target.value)} maxLength={6} /></div>
                  <div><label className="label">Data (MMAA)</label><input className="input font-mono w-32" value={form.mmaa ?? ''} onChange={e => f('mmaa', e.target.value)} maxLength={4} /></div>
                </>
              )}
              <div><label className="label">CAB del guasto trattenuto (facoltativo)</label><input className="input font-mono" value={form.cab_origine ?? ''} onChange={e => f('cab_origine', e.target.value.toUpperCase())} /></div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div><label className="label">Riferimento lavoro</label><input className="input" value={form.riferimento_lavoro ?? ''} onChange={e => f('riferimento_lavoro', e.target.value)} /></div>
            <div><label className="label">Matricola produttore</label><input className="input" value={form.matricola_produttore ?? ''} onChange={e => f('matricola_produttore', e.target.value)} /></div>
          </div>
          <div><label className="label">Note</label><textarea className="input" rows={2} value={form.note ?? ''} onChange={e => f('note', e.target.value)} /></div>
        </div>

        {errori.length > 0 && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 space-y-1">
            {errori.map((e, i) => <p key={i}>⚠ {e}</p>)}
          </div>
        )}
        {avvisi.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 space-y-2">
            {avvisi.map((a, i) => (
              <label key={i} className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={avvisiConfermati.has(a)}
                  onChange={e => setAvvisiConfermati(prev => {
                    const next = new Set(prev)
                    if (e.target.checked) next.add(a); else next.delete(a)
                    return next
                  })} />
                <span>{a}</span>
              </label>
            ))}
          </div>
        )}

        {operazione !== 'NON_RIPARABILE' && (
          <div className="card p-4 flex items-end gap-3 flex-wrap">
            <div>
              <label className="label">Copie</label>
              <input className="input w-20" type="number" min={1} max={99} value={copie} onChange={e => setCopie(parseInt(e.target.value, 10) || 1)} />
            </div>
            <div>
              <label className="label">Stampante</label>
              <select className="select w-48" value={stampanteId} onChange={e => setStampanteId(e.target.value ? parseInt(e.target.value, 10) : '')}>
                <option value="">— Nessuna (solo registra) —</option>
                {(stampanti ?? []).map((s: any) => <option key={s.id} value={s.id}>{s.nome}{s.predefinita ? ' ★' : ''}</option>)}
              </select>
            </div>
            <button className="btn-primary flex items-center gap-1.5" disabled={disabilitaGenera || (avvisi.length > 0 && !tuttiConfermati)} onClick={() => creaMut.mutate()}>
              <Printer size={14} /> {stampanteId ? 'Genera e stampa' : 'Genera senza stampare'}
            </button>
          </div>
        )}
        {operazione === 'NON_RIPARABILE' && (
          <button className="btn-primary" disabled={!form.motivo || creaMut.isPending} onClick={() => creaMut.mutate()}>Registra</button>
        )}

        {inviata && <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">Etichetta {inviata.stampante}.</div>}

        {operazione === 'ANTICIPO_RIPARAZIONE' && ultimaEtichettaId && (
          <button className="btn-secondary flex items-center gap-1.5" disabled={modelloTrattenutoMut.isPending} onClick={() => modelloTrattenutoMut.mutate()}>
            <Download size={14} /> Genera modello PDF "materiale trattenuto"
          </button>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-600">Anteprima</h2>
        <div className="card p-6 flex items-center justify-center min-h-[200px] bg-slate-50">
          {caricandoAnteprima ? (
            <span className="text-sm text-slate-400">Caricamento...</span>
          ) : anteprima?.svg ? (
            <div dangerouslySetInnerHTML={{ __html: anteprima.svg }} style={{ maxWidth: '100%' }} />
          ) : (
            <span className="text-sm text-slate-400">Compila i campi per generare l'anteprima</span>
          )}
        </div>
        {anteprima?.codice && (
          <div className="card p-4 text-sm space-y-1">
            <p className="font-mono text-slate-700">{anteprima.codice}</p>
            <p className="text-slate-500">{anteprima.campi?.testo_in_chiaro}</p>
          </div>
        )}
      </div>
    </div>
  )
}
